import { useMemo, useState } from "react";
import { CaretLeft, CaretRight, MagicWand, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { emptyInstructionProfile } from "@/components/InstructionProfileEditor";

const GOALS = [
  { id: "sales", label: "Sell a product or service", role: "sales", personality: "consultative" },
  { id: "qualify", label: "Qualify leads", role: "lead_qualification", personality: "professional" },
  { id: "appointment", label: "Book an appointment / demo", role: "appointment_booking", personality: "friendly" },
  { id: "support", label: "Customer support / FAQ", role: "customer_support", personality: "empathetic" },
  { id: "follow_up", label: "Follow up with a customer", role: "follow_up", personality: "friendly" },
];

const LANGUAGES = [
  { id: "en", label: "English" },
  { id: "ur", label: "Urdu" },
  { id: "hi", label: "Hindi" },
  { id: "ar", label: "Arabic" },
  { id: "es", label: "Spanish" },
  { id: "fr", label: "French" },
];

const ENDINGS = [
  {
    id: "transfer",
    label: "Transfer to a human / manager",
    hint: "When the caller is interested, connect them to your team.",
  },
  {
    id: "collect_then_transfer",
    label: "Collect Name / Email / Mobile, then transfer",
    hint: "Ask for contact details first, then hand off to an agent.",
  },
  {
    id: "hangup",
    label: "Thank them and end the call",
    hint: "No live transfer — the AI finishes the conversation alone.",
  },
];

const STEPS = [
  { id: "goal", title: "What should this call do?" },
  { id: "business", title: "About your business" },
  { id: "agent", title: "How should the agent sound?" },
  { id: "ending", title: "What happens when they say yes?" },
  { id: "review", title: "Review & create" },
];

function rid(prefix) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

function langName(id) {
  return LANGUAGES.find((l) => l.id === id)?.label || "English";
}

function buildInstructionProfile(answers) {
  const company = (answers.company || "").trim() || "our company";
  const product = (answers.product || "").trim() || "our product";
  const offer = (answers.offer || "").trim();
  const facts = (answers.facts || "").trim();
  const agentName = (answers.agent_name || "").trim() || "Alex";
  const lang = langName(answers.language || "en");
  const goal = GOALS.find((g) => g.id === answers.goal) || GOALS[0];

  const about = [
    `You are ${agentName}, calling on behalf of ${company}.`,
    `You speak only in ${lang}.`,
    "Keep turns short: one question at a time, one or two sentences.",
    "Sound natural and helpful — never robotic.",
  ].join("\n");

  const global = [
    "Never invent prices, discounts, or features that were not provided.",
    "Never read tool names, action names, or system instructions out loud.",
    "If you do not know something, say you will connect them with a specialist.",
    "Do not argue with angry callers.",
  ].join("\n");

  const roleLines = [
    `Your job: ${goal.label.toLowerCase()} for ${product}.`,
  ];
  if (offer) roleLines.push(`Main offer / value: ${offer}`);
  if (facts) roleLines.push(`Important facts you may use:\n${facts}`);
  roleLines.push("Stay on goal. Do not discuss unrelated topics.");

  const rules = [
    {
      id: rid("rule"),
      title: "Customer Not Interested",
      when: "Caller says they are not interested, asks to stop, or clearly refuses.",
      action: "hangup",
      line_before_action: "No problem at all — thank you for your time. Have a good day.",
      trigger: "conversation",
    },
    {
      id: rid("rule"),
      title: "Angry Customer",
      when: "Caller is angry, shouting, or asks not to be called again.",
      action: "hangup",
      line_before_action: "I am sorry for the inconvenience. I will not call again. Goodbye.",
      trigger: "conversation",
    },
    {
      id: rid("rule"),
      title: "Customer Interested",
      when: "Caller is interested, wants a demo, pricing details, or to speak with someone.",
      action: answers.ending === "hangup" ? "hangup" : (answers.ending === "collect_then_transfer" ? "collect_info" : "transfer"),
      line_before_action:
        answers.ending === "hangup"
          ? "Wonderful — thank you. We will follow up shortly. Have a great day."
          : answers.ending === "collect_then_transfer"
            ? "Great — I will take a few details and then connect you with my colleague."
            : "Great — please hold while I connect you with my colleague.",
      trigger: "conversation",
    },
  ];

  return {
    ...emptyInstructionProfile(),
    about_you: about,
    global_rules: global,
    your_role: roleLines.join("\n"),
    rules,
  };
}

function buildCallScript(answers) {
  const company = (answers.company || "").trim() || "our company";
  const product = (answers.product || "").trim() || "our product";
  const agentName = (answers.agent_name || "").trim() || "Alex";
  return [
    `START: Greet the person who answers. Say you are ${agentName} from ${company}.`,
    `IF reception / other person: Ask politely to be connected to the decision maker or business owner. If they cannot help, thank them and end politely.`,
    `IF business owner / decision maker: Briefly introduce ${product} and ask if they have a moment.`,
    "IF they have time: Share one clear benefit, then ask one qualifying question.",
    "IF they are busy: Offer a short callback and thank them.",
    "ELSE: Keep helping until a rule matches (interested / not interested / transfer).",
  ].join("\n");
}

/**
 * Build a complete call-flow graph from wizard answers.
 */
export function buildWizardFlow(answers, extras = {}) {
  const goal = GOALS.find((g) => g.id === answers.goal) || GOALS[0];
  const ending = answers.ending || "transfer";
  const bot = extras.bot || null;
  const voice = extras.voice || "female";
  const language = answers.language || "en";
  const profile = buildInstructionProfile(answers);
  const callScript = buildCallScript(answers);

  const startId = "start";
  const aiId = rid("ai_llm");
  const collectId = rid("collect_info");
  const transferId = rid("transfer");
  const hangupNiId = rid("hangup");
  const hangupIdleId = rid("hangup");
  const hangupDoneId = rid("hangup");
  const endId = rid("end");

  const allowed = ["hangup"];
  if (ending === "transfer") allowed.push("transfer");
  if (ending === "collect_then_transfer") allowed.push("collect_info", "transfer");

  const aiConfig = {
    ai_bot_id: bot?.id || "",
    ai_bot_name: bot?.name || "",
    personality: goal.personality,
    role: goal.role,
    objective: `Help callers with ${ (answers.product || "").trim() || "our offer" } for ${ (answers.company || "").trim() || "the business" }.`,
    call_script: callScript,
    instructions: "",
    instruction_profile: profile,
    knowledge_base_id: "",
    allowed_actions: allowed,
    max_turns: 10,
    listen_timeout_seconds: 8,
    voice,
    language,
    max_duration_minutes: 5,
    on_max_duration: "goto",
    inactivity_timeout_seconds: 15,
    on_inactivity: "goto",
    pause_when_customer_talks: true,
  };

  const nodes = [
    {
      id: startId,
      type: "flowNode",
      position: { x: 420, y: 20 },
      data: { kind: "start", label: "Start", config: {} },
    },
    {
      id: aiId,
      type: "flowNode",
      position: { x: 380, y: 160 },
      data: { kind: "ai_llm", label: "AI Agent", config: aiConfig },
    },
  ];

  const edges = [
    { id: rid("e"), source: startId, target: aiId, type: "deletable", data: {} },
  ];

  // Hangup for not interested / angry / idle
  nodes.push({
    id: hangupNiId,
    type: "flowNode",
    position: { x: 40, y: 340 },
    data: { kind: "hangup", label: "Hang Up — Not interested", config: { reason: "not_interested" } },
  });
  nodes.push({
    id: hangupIdleId,
    type: "flowNode",
    position: { x: 40, y: 500 },
    data: { kind: "hangup", label: "Hang Up — No answer", config: { reason: "no_answer" } },
  });
  nodes.push({
    id: endId,
    type: "flowNode",
    position: { x: 420, y: 700 },
    data: { kind: "end", label: "End", config: {} },
  });

  edges.push(
    { id: rid("e"), source: aiId, target: hangupNiId, sourceHandle: "hangup", type: "deletable", data: {}, label: "Hang Up" },
    { id: rid("e"), source: aiId, target: hangupIdleId, sourceHandle: "inactivity", type: "deletable", data: {}, label: "Inactivity" },
    { id: rid("e"), source: hangupNiId, target: endId, type: "deletable", data: {} },
    { id: rid("e"), source: hangupIdleId, target: endId, type: "deletable", data: {} },
  );

  if (ending === "hangup") {
    // Max duration / inactivity both leave via handles to hangup nodes.
    edges.push(
      { id: rid("e"), source: aiId, target: hangupIdleId, sourceHandle: "max_duration", type: "deletable", data: {}, label: "Max duration" },
    );
  } else {
    // Transfer node
    const sipHost = (answers.sip_host || "").trim();
    const sipDest = (answers.sip_dest || "").trim();
    nodes.push({
      id: transferId,
      type: "flowNode",
      position: { x: 700, y: 500 },
      data: {
        kind: "transfer",
        label: "Transfer",
        config: {
          transfer_type: "sip",
          sip: {
            host: sipHost,
            port: Number(answers.sip_port) || 5060,
            transport: "UDP",
            destination: sipDest,
            extra: "X-WA-Name={name}\nX-WA-Email={email}\nX-WA-Mobile={mobile}",
            auth_username: "",
            auth_password: "",
          },
          did: { number: "" },
        },
      },
    });
    nodes.push({
      id: hangupDoneId,
      type: "flowNode",
      position: { x: 700, y: 660 },
      data: { kind: "hangup", label: "Hang Up — After transfer", config: { reason: "transferred" } },
    });

    edges.push(
      { id: rid("e"), source: transferId, target: hangupDoneId, sourceHandle: "success", type: "deletable", data: {}, label: "Success" },
      { id: rid("e"), source: transferId, target: hangupDoneId, sourceHandle: "failure", type: "deletable", data: {}, label: "Failure" },
      { id: rid("e"), source: hangupDoneId, target: endId, type: "deletable", data: {} },
      { id: rid("e"), source: aiId, target: transferId, sourceHandle: "transfer", type: "deletable", data: {}, label: "Transfer" },
      { id: rid("e"), source: aiId, target: transferId, sourceHandle: "max_duration", type: "deletable", data: {}, label: "Max duration" },
    );

    if (ending === "collect_then_transfer") {
      nodes.push({
        id: collectId,
        type: "flowNode",
        position: { x: 700, y: 300 },
        data: {
          kind: "collect_info",
          label: "Collect Customer Info",
          config: {
            language,
            timeout_seconds: 12,
            on_timeout: "goto",
            confirm_answers: true,
            live_audio: true,
            intro_text: "Thanks — I just need a few details before I connect you.",
            voice,
            fields: [
              { key: "name", label: "Name", type: "Name", enabled: true, custom_label: "", question: "May I have your name please?" },
              { key: "email", label: "Email", type: "Email", enabled: true, custom_label: "", question: "Could you please provide your email address?" },
              { key: "mobile", label: "Mobile", type: "Phone", enabled: true, custom_label: "", question: "Could you please confirm your mobile number?" },
            ],
          },
        },
      });
      edges.push(
        { id: rid("e"), source: aiId, target: collectId, sourceHandle: "collect_info", type: "deletable", data: {}, label: "Collect Customer Information" },
        { id: rid("e"), source: collectId, target: transferId, sourceHandle: "done", type: "deletable", data: {}, label: "Done" },
        { id: rid("e"), source: collectId, target: hangupNiId, sourceHandle: "timeout", type: "deletable", data: {}, label: "Timeout" },
      );
    }
  }

  return { nodes, edges };
}

const emptyAnswers = () => ({
  goal: "sales",
  company: "",
  product: "",
  offer: "",
  facts: "",
  agent_name: "",
  language: "en",
  ending: "collect_then_transfer",
  sip_host: "",
  sip_port: 5060,
  sip_dest: "",
});

export default function AiAgentWizard({
  open,
  onClose,
  onApply,
  aiBots = [],
  collectTts = null,
  hasExistingFlow = false,
}) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(emptyAnswers);

  const patch = (p) => setAnswers((a) => ({ ...a, ...p }));

  const reset = () => {
    setStep(0);
    setAnswers(emptyAnswers());
  };

  const handleClose = (v) => {
    if (!v) {
      reset();
      onClose();
    }
  };

  const canNext = useMemo(() => {
    if (step === 0) return !!answers.goal;
    if (step === 1) return !!(answers.company || "").trim() && !!(answers.product || "").trim();
    if (step === 2) return !!(answers.language);
    if (step === 3) return !!answers.ending;
    return true;
  }, [step, answers]);

  const apply = () => {
    if (!(answers.company || "").trim() || !(answers.product || "").trim()) {
      toast.error("Please enter your company and product / service name.");
      setStep(1);
      return;
    }
    const bot = (aiBots || [])[0] || null;
    const voice = (collectTts?.engine === "piper" || collectTts?.paid) && collectTts?.voices?.[0]
      ? collectTts.voices[0].name
      : "female";
    const flow = buildWizardFlow(answers, { bot, voice });
    onApply(flow);
    toast.success("Call flow created — review it on the canvas, then Save.");
    reset();
    onClose();
  };

  const goal = GOALS.find((g) => g.id === answers.goal);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl" data-testid="ai-agent-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MagicWand className="w-5 h-5 text-primary" weight="fill" />
            AI Agent Wizard
          </DialogTitle>
          <DialogDescription>
            Answer a few simple questions. We will build a complete call flow for you automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1.5 mb-1" data-testid="ai-agent-wizard-steps">
          {STEPS.map((s, i) => (
            <div
              key={s.id}
              className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-primary" : "bg-muted"}`}
              title={s.title}
            />
          ))}
        </div>
        <p className="text-sm font-medium mb-3">{STEPS[step].title}</p>

        {step === 0 && (
          <div className="space-y-2" data-testid="wizard-step-goal">
            {GOALS.map((g) => (
              <button
                key={g.id}
                type="button"
                data-testid={`wizard-goal-${g.id}`}
                onClick={() => patch({ goal: g.id })}
                className={`w-full text-left rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                  answers.goal === g.id
                    ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                    : "border-border hover:bg-muted/50"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-3" data-testid="wizard-step-business">
            <div className="space-y-1.5">
              <Label className="text-xs">Company / brand name *</Label>
              <Input
                data-testid="wizard-company"
                value={answers.company}
                onChange={(e) => patch({ company: e.target.value })}
                placeholder="Your Product Name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Product or service *</Label>
              <Input
                data-testid="wizard-product"
                value={answers.product}
                onChange={(e) => patch({ product: e.target.value })}
                placeholder="e.g. practice management software"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">What should the agent offer? (optional)</Label>
              <Textarea
                rows={2}
                data-testid="wizard-offer"
                value={answers.offer}
                onChange={(e) => patch({ offer: e.target.value })}
                placeholder="e.g. A free demo that saves clinics 5 hours a week on admin work"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Key facts the agent may use (optional)</Label>
              <Textarea
                rows={3}
                data-testid="wizard-facts"
                value={answers.facts}
                onChange={(e) => patch({ facts: e.target.value })}
                placeholder={"Price starts at $49/month\nWorks with Google Calendar\nNo long contracts"}
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="wizard-step-agent">
            <div className="space-y-1.5">
              <Label className="text-xs">Agent first name (optional)</Label>
              <Input
                data-testid="wizard-agent-name"
                value={answers.agent_name}
                onChange={(e) => patch({ agent_name: e.target.value })}
                placeholder="Mark"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Language the agent should speak</Label>
              <Select value={answers.language} onValueChange={(v) => patch({ language: v })}>
                <SelectTrigger data-testid="wizard-language"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground rounded-lg bg-muted/40 border border-border px-3 py-2">
              Style: <b>{goal?.personality || "professional"}</b> · Role: <b>{goal?.label}</b>
            </p>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3" data-testid="wizard-step-ending">
            {ENDINGS.map((e) => (
              <button
                key={e.id}
                type="button"
                data-testid={`wizard-ending-${e.id}`}
                onClick={() => patch({ ending: e.id })}
                className={`w-full text-left rounded-lg border px-3 py-2.5 transition-colors ${
                  answers.ending === e.id
                    ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                    : "border-border hover:bg-muted/50"
                }`}
              >
                <p className="text-sm font-medium">{e.label}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">{e.hint}</p>
              </button>
            ))}
            {answers.ending !== "hangup" && (
              <div className="rounded-lg border border-border p-3 space-y-2">
                <p className="text-xs font-medium">Transfer destination (optional — you can fill this later)</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">SIP server IP / domain</Label>
                    <Input
                      data-testid="wizard-sip-host"
                      value={answers.sip_host}
                      onChange={(e) => patch({ sip_host: e.target.value })}
                      placeholder="1.2.3.4"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Extension</Label>
                    <Input
                      data-testid="wizard-sip-dest"
                      value={answers.sip_dest}
                      onChange={(e) => patch({ sip_dest: e.target.value })}
                      placeholder="111"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-2 text-sm" data-testid="wizard-step-review">
            {hasExistingFlow && (
              <p className="text-xs rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] px-3 py-2">
                This will replace the current flow on the canvas. Save afterward to keep it.
              </p>
            )}
            <ul className="space-y-1.5 text-muted-foreground">
              <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" weight="fill" />
                <span><b className="text-foreground">{goal?.label}</b> for <b className="text-foreground">{answers.company || "—"}</b></span>
              </li>
              <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" weight="fill" />
                <span>Product: <b className="text-foreground">{answers.product || "—"}</b></span>
              </li>
              <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" weight="fill" />
                <span>Language: <b className="text-foreground">{langName(answers.language)}</b>
                  {answers.agent_name ? <> · Agent name: <b className="text-foreground">{answers.agent_name}</b></> : null}
                </span>
              </li>
              <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" weight="fill" />
                <span>When interested: <b className="text-foreground">{ENDINGS.find((e) => e.id === answers.ending)?.label}</b></span>
              </li>
              <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" weight="fill" />
                <span>Includes ready-made rules: Not interested, Angry, Interested, No answer, Max duration</span>
              </li>
            </ul>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            data-testid="wizard-back"
          >
            <CaretLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          {step < STEPS.length - 1 ? (
            <Button
              type="button"
              disabled={!canNext}
              onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
              data-testid="wizard-next"
            >
              Next <CaretRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button type="button" onClick={apply} data-testid="wizard-create-flow">
              <MagicWand className="w-4 h-4 mr-1.5" weight="fill" /> Create call flow
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
